// Helper function to convert string values to boolean
function stringToBoolean(value) {
  if (typeof value !== 'string') {
    return Boolean(value);
  }
  
  const lowercaseValue = value.toLowerCase();
  if (lowercaseValue === 'true' || lowercaseValue === '1') {
    return true;
  }
  if (lowercaseValue === 'false' || lowercaseValue === '0') {
    return false;
  }
  
  return Boolean(value);
}

// Feature flag parser function
function parseFeatureFlags() {
  // Parse query params
  const queryParams = new URLSearchParams(window.location.search);
  const featureFlags = {};
  
  // Extract all ff_* query parameters
  for (const [key, value] of queryParams.entries()) {
    if (key.startsWith('ff_')) {
      const boolValue = stringToBoolean(value);
      featureFlags[key.replace('ff_', '')] = boolValue;
      
      // Store in localStorage
      localStorage.setItem(key, boolValue);
    }
  }
  
  return featureFlags;
}

// Load feature flags from localStorage
function loadFeatureFlagsFromStorage() {
  const featureFlags = {};
  
  // Iterate through localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith('ff_')) {
      const value = localStorage.getItem(key);
      featureFlags[key.replace('ff_', '')] = stringToBoolean(value);
    }
  }
  
  return featureFlags;
}

class ConfigService {
  constructor() {
    this.config = {};
    this.initialized = false;
  }

  // Initialize the config (can be called at app start)
  init(initialConfig = {}) {
    if (this.initialized) {
      console.warn('ConfigService already initialized. To update config, use update() method instead.');
      return;
    }
    
    // First load any FF values from localStorage
    const storedFlags = loadFeatureFlagsFromStorage();
    
    // Then check for query params (they take precedence over localStorage)
    const queryFlags = parseFeatureFlags();
    
    // Merge everything with initialConfig having lowest precedence
    this.config = { 
      ...initialConfig,
      ...storedFlags, 
      ...queryFlags 
    };
    
    this.initialized = true;
    console.info('ConfigService initialized with feature flags');
    
    // Log feature flags found
    const allFlags = { ...storedFlags, ...queryFlags };
    if (Object.keys(allFlags).length > 0) {
      console.info('Applied feature flags:', allFlags);
    }
  }

  // Update existing config
  update(newConfig) {
    this.config = { ...this.config, ...newConfig };
    console.info('ConfigService updated');
  }

  // Get a config value
  get(key, defaultValue) {
    if(key === 'performance_v1') {
      return true;
    }

    if (!this.initialized) {
      console.warn('ConfigService not initialized. Call init() first.');
    }

    if(key === 'performance_v1') {
      const listOfHosts = ['professional-opt.coolremote.net'];
      if(listOfHosts.includes(window.location.host)) {
        return true;
      }
    }

    const value = this.config[key];
    
    if (value === undefined) {
      if (defaultValue !== undefined) {
        return defaultValue;
      }
      console.error(`Config key "${key}" not found and no default value provided`);
    }
    
    // Handle function config values
    if (typeof value === 'function') {
      return value();
    }
    
    return value;
  }

  // Check if a config key exists
  has(key) {
    return key in this.config;
  }

  // Get all config keys
  getAllKeys() {
    return Object.keys(this.config);
  }
  
  // Get all feature flag keys
  getFeatureFlags() {
    return Object.keys(this.config).filter(key => key.startsWith('ff_'));
  }
  
  // Set a feature flag
  setFeatureFlag(flagName, value) {
    const key = flagName.startsWith('ff_') ? flagName : `ff_${flagName}`;
    const boolValue = stringToBoolean(value);
    
    // Update config
    this.config[key] = boolValue;
    
    // Update localStorage
    localStorage.setItem(key, boolValue);
    
    return boolValue;
  }

  // Clear all config
  clear() {
    this.config = {};
    console.info('ConfigService cleared');
  }
  
  // Clear only feature flags (from both config and localStorage)
  clearFeatureFlags() {
    // Find all feature flag keys
    const featureFlagKeys = this.getFeatureFlags();
    
    // Remove from config
    featureFlagKeys.forEach(key => {
      delete this.config[key];
      localStorage.removeItem(key);
    });
    
    console.info(`Cleared ${featureFlagKeys.length} feature flags`);
  }
}

// Create singleton instance using IIFE
const ConfigServiceInstance = (function() {
  let instance;
  
  function createInstance() {
    return new ConfigService();
  }
  
  return {
    getInstance: function() {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    }
  };
})();

// Export the singleton
export default ConfigServiceInstance.getInstance();